import { Flex, Input } from '@chakra-ui/react'

import { ActionButton } from '../../../../components/ActionButton'
import { FormField } from '../../../../components/FormField'
import { isRichTextEmpty, RichTextField } from '../../../../components/RichTextField'
import { SectionCard } from '../../../../components/SectionCard'
import { ICourseBonusFields } from '../../../../services/CoursesOnline/interfaces'

/** Limite de bônus por curso (validado também na API). */
export const BONUSES_MAX = 10

export const baseBonus = (): ICourseBonusFields => ({
  _id: crypto.randomUUID(),
  title: '',
  description: ''
})

/** Mesma regra da API: até {@link BONUSES_MAX} bônus, e a descrição é obrigatória; o título pode ficar vazio. */
export const validateBonuses = (bonuses: ICourseBonusFields[]): Record<string, string[]> => {
  const errors: Record<string, string[]> = {}
  if (bonuses.length > BONUSES_MAX) {
    errors.bonuses = [`O curso pode ter no máximo ${BONUSES_MAX} bônus.`]
  }
  bonuses.forEach((bonus, index) => {
    if (isRichTextEmpty(bonus.description)) {
      errors[`bonuses.${index}.description`] = ['A descrição do bônus é obrigatória.']
    }
  })
  return errors
}

interface IProps {
  bonuses: ICourseBonusFields[]
  onChange: (next: ICourseBonusFields[]) => void
  disabled?: boolean
  /** Erros por caminho com ponto (`bonuses.0.description`), como a API devolve. */
  fieldErrors?: Record<string, string[]>
}

/** Bônus da página do curso gravado (ABRA-437), na ordem em que o site mostra. */
export function CourseBonusesEditor({ bonuses, onChange, disabled, fieldErrors }: IProps) {
  const add = () => onChange([...bonuses, baseBonus()])

  const update = (id: string, field: 'title' | 'description', value: string) =>
    onChange(bonuses.map((b) => (b._id === id ? { ...b, [field]: value } : b)))

  const remove = (id: string) => onChange(bonuses.filter((b) => b._id !== id))

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= bonuses.length) return
    const next = [...bonuses]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  const errorFor = (index: number, field: 'title' | 'description') => fieldErrors?.[`bonuses.${index}.${field}`]?.[0]

  return (
    <>
      {bonuses.map((bonus, index) => (
        <SectionCard
          key={bonus._id}
          variant="tile"
          title={`Bônus ${index + 1}`}
          titleAs="h3"
          actions={
            <Flex align="center" gap={1}>
              <ActionButton
                action="move-up"
                label="Mover bônus para cima"
                mr={0}
                onClick={() => move(index, -1)}
                isDisabled={disabled || index === 0}
              />
              <ActionButton
                action="move-down"
                label="Mover bônus para baixo"
                mr={0}
                onClick={() => move(index, 1)}
                isDisabled={disabled || index === bonuses.length - 1}
              />
              <ActionButton
                action="delete"
                label="Remover bônus"
                variant="ghost"
                mr={0}
                onClick={() => remove(bonus._id)}
                isDisabled={disabled}
              />
            </Flex>
          }
          mt={3}
        >
          <Flex direction="column" gap={3}>
            <FormField
              label="Título"
              error={errorFor(index, 'title')}
              helperText='Vazio, o site mostra "Módulo Bônus".'
              isDisabled={disabled}
            >
              <Input
                size="sm"
                value={bonus.title}
                onChange={(e) => update(bonus._id, 'title', e.target.value)}
                maxLength={255}
                placeholder="Ex.: Pack de pincéis exclusivos"
              />
            </FormField>

            <RichTextField
              label="Descrição"
              error={errorFor(index, 'description')}
              isRequired
              value={bonus.description}
              onChange={(description) => update(bonus._id, 'description', description)}
              placeholder="O que o aluno recebe neste bônus..."
              isReadOnly={disabled}
            />
          </Flex>
        </SectionCard>
      ))}

      <FormField error={fieldErrors?.bonuses} helperText={`Até ${BONUSES_MAX} bônus.`} mt={3}>
        <ActionButton
          action="create"
          label="Adicionar bônus"
          showLabel
          hideTooltip
          onClick={add}
          isDisabled={disabled || bonuses.length >= BONUSES_MAX}
        />
      </FormField>
    </>
  )
}
